<template>
  <div class="sv-toolbar" data-test="sv-toolbar">
    <!--
      ‹ date › as ONE bordered group (the design's `.date-picker`). The date itself is the native
      <input type="date">, per the codebase convention — NOT b-form-datepicker and NOT the design's
      hand-built calendar. It gives the platform's own picker (including the one iPad users already
      know) for free.
    -->
    <div class="sv-toolbar__group">
      <button
        type="button"
        class="sv-toolbar__nav"
        aria-label="Previous day"
        data-test="sv-prev-day"
        @click="shift(-1)"
      ><i class="fa fa-angle-left" aria-hidden="true"></i></button>

      <label class="sv-toolbar__datelabel">
        <span class="sr-only">Schedule date</span>
        <input
          type="date"
          class="sv-toolbar__date"
          :value="date"
          data-test="sv-date"
          @change="$emit('change-date', $event.target.value)"
        />
      </label>

      <button
        type="button"
        class="sv-toolbar__nav"
        aria-label="Next day"
        data-test="sv-next-day"
        @click="shift(1)"
      ><i class="fa fa-angle-right" aria-hidden="true"></i></button>
    </div>

    <!--
      The day in words — "Friday, Oct 23, 2026" — with a TODAY pill when it is today. This replaced
      the old separate "Friday, October 23" text: the weekday and the year are what tell a front desk
      they are not looking at last week's page.
    -->
    <span class="sv-toolbar__day" data-test="sv-day-label">
      <span class="sv-toolbar__daytext" data-test="sv-day-text">{{ dayLabel }}</span>
      <span v-if="isToday" class="sv-toolbar__pill" data-test="sv-today-pill">Today</span>
    </span>

    <!--
      Only when NOT on today (a button that jumps to the day you are already on is noise), and never
      on a phone: a phone's row has no room, and the ‹ › arrows are one tap from any day.
    -->
    <button
      v-if="!isToday && !compact"
      type="button"
      class="sv-toolbar__btn"
      data-test="sv-today"
      @click="$emit('change-date', today)"
    >Today</button>

    <span class="sv-toolbar__spacer"></span>

    <!-- The page's freshness indicator ("Live" / "Last synced 15 min ago") goes here. -->
    <slot></slot>

    <button
      type="button"
      class="sv-toolbar__btn"
      :disabled="busy"
      data-test="sv-refresh"
      @click="$emit('refresh')"
    >{{ busy ? 'Refreshing…' : 'Refresh' }}</button>
  </div>
</template>

<script>
/**
 * The date row of the page (the design's `.tb-date`): ‹ date › · TODAY · [Today button] · spacer ·
 * [freshness slot] · Refresh. The filter row beneath it is the page's own (`.sv-controls`).
 *
 * Day arithmetic is done on the YYYY-MM-DD string with moment's UTC parsing, never on a local
 * Date: `new Date('2026-09-19')` is parsed as UTC midnight and then rendered in the viewer's
 * timezone, so "yesterday" lands on the wrong day for anyone west of UTC. The date shown here is
 * the PRACTICE's day (see the store's todayForSite).
 */
import moment from 'moment-timezone';

export default {
  name: 'ScheduleToolbar',

  props: {
    /** YYYY-MM-DD, the day being viewed, in the site's timezone. */
    date: { type: String, required: true },
    /** YYYY-MM-DD for today in the site's timezone. */
    today: { type: String, required: true },
    busy: { type: Boolean, default: false },
    /**
     * A phone: no "Today" button (see the template). Decided by the PAGE's phone breakpoint in JS,
     * like the view switch — not by hiding it with CSS.
     */
    compact: { type: Boolean, default: false },
  },

  computed: {
    isToday() {
      return this.date === this.today;
    },

    /**
     * "Friday, Oct 23, 2026" — and on a phone "Fri, Oct 23" (the year AND the weekday's tail are dropped:
     * the second line also carries TODAY and, when the sync is behind, "Last synced 125 min ago", and
     * "Wednesday, Sep 30" + TODAY + that chip does not fit a ~303px page on ONE line — it was clipped
     * to "Wednesday, S…". The native input above it still shows the full date). '' for a date that does
     * not parse.
     */
    dayLabel() {
      const m = moment.utc(this.date, 'YYYY-MM-DD', true);
      if (!m.isValid()) return '';
      return m.format(this.compact ? 'ddd, MMM D' : 'dddd, MMM D, YYYY');
    },
  },

  methods: {
    shift(days) {
      const m = moment.utc(this.date, 'YYYY-MM-DD', true);
      if (!m.isValid()) return;
      this.$emit('change-date', m.add(days, 'day').format('YYYY-MM-DD'));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

.sv-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

// ‹ date ›: one 38px bordered pill, the soft shadow, a hairline between the arrows and the date.
.sv-toolbar__group {
  display: inline-flex;
  align-items: stretch;
  height: 38px;
  box-sizing: border-box;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  box-shadow: $sv-shadow-1;
  overflow: hidden;
}

.sv-toolbar__nav {
  width: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: $sv-ink-2;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;

  &:first-child { border-right: 1px solid $sv-line; }
  &:last-child { border-left: 1px solid $sv-line; }
  &:hover { background: $sv-bg-3; color: $sv-ink; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid $sv-brand-ring; outline-offset: -2px; }
}

.sv-toolbar__datelabel {
  display: flex;
  align-items: stretch;
  margin: 0;
}

.sv-toolbar__date {
  // 16px in px, not rem: below 16px iOS Safari zooms in on focus and never zooms back out.
  font-size: 16px;
  font-weight: 600;
  min-width: 0;
  padding: 0 10px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: $sv-ink;

  &:focus { outline: none; background: $sv-brand-subtle; }
}

.sv-toolbar__day {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin-left: 2px;
  color: $sv-ink;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

.sv-toolbar__daytext {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

// TODAY: teal-subtle, 10px / 700, a pill.
.sv-toolbar__pill {
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: $sv-radius-pill;
  background: $sv-brand-subtle;
  color: $sv-brand-deep;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

// "Today" and "Refresh": the design's `.btn-38`.
.sv-toolbar__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 15px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  box-shadow: $sv-shadow-1;
  color: $sv-ink;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:hover:not(:disabled) { background: $sv-bg; border-color: $sv-line-strong; }
  &:disabled { color: $sv-ink-3; cursor: default; }
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid $sv-brand-ring; outline-offset: 2px; }
}

.sv-toolbar__spacer {
  flex: 1 1 auto;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// Phone: TWO lines, never three — every row of chrome pushes the first appointment further down a
// 390px screen. Line 1 is `‹ [native date] ›` with Refresh pushed to the right edge; line 2 is the
// day in words (13px) with its TODAY pill, and the page's sync chip beside them when it is NOT live
// (the healthy "Live" is not drawn on a phone at all). One DOM, reordered by `order`: the group and
// Refresh come first, then a full-width break (the ::after item) that forces the wrap, then the day
// and the sync chip. The order is the markup's, not a second copy of it.
@media (max-width: $sv-phone-max) {
  .sv-toolbar { gap: 6px 8px; margin-bottom: 8px; }

  // The group takes whatever Refresh leaves (a 0 flex-basis, so it never forces Refresh onto another line) and may SHRINK below its natural 225px: with the classic
  // sidebar a 390px phone gives the page only ~303px, and ‹ date › (225) + Refresh (78) + gap did not
  // fit — Refresh dropped to a line of its own and the date block became three lines. The arrows and the
  // input's padding give up a few px each; the input stays 16px (iOS focus zoom).
  .sv-toolbar__group { order: 0; flex: 1 1 0%; min-width: 0; }

  .sv-toolbar__datelabel { flex: 1 1 auto; min-width: 0; }

  .sv-toolbar__nav { width: 32px; }

  .sv-toolbar__date {
    width: 100%;
    min-width: 0;
    padding: 0 4px;
  }

  .sv-toolbar__btn[data-test="sv-refresh"] { order: 1; margin-left: auto; }

  // The line break: a zero-height item as wide as the row, after Refresh and before everything on line 2.
  .sv-toolbar::after {
    content: '';
    order: 2;
    flex: 0 0 100%;
    height: 0;
  }

  // The day takes whatever the sync chip leaves of line 2 (a 0 flex-basis, so — like the group above —
  // it can never be the reason the chip wraps onto a THIRD line): with the classic sidebar the page is
  // only ~303px wide, and "Friday, Oct 23" + TODAY (140px) beside "Last synced 125 min ago" (171px) was
  // 319px. The day text gives way first (its ellipsis, only ever reached by a 4-digit minute count);
  // the pill and the chip never do.
  .sv-toolbar__day {
    order: 3;
    flex: 1 1 0%;
    gap: 6px;
    margin-left: 2px;
    font-size: 13px;
  }

  .sv-toolbar__pill { padding: 2px 6px; }

  .sv-toolbar__spacer { display: none; }
}
</style>
